/**
 * undercover® "zie jij de helm?" voor/na-slider.
 * Slepen met muis/touch/pen (pointer events), toetsenbord op de handle
 * (pijlen ±5, PageUp/PageDown ±10, Home/End), klikken op het beeld springt ernaartoe.
 * Eén keer een kleine hint-beweging als de slider in beeld komt (niet bij reduced motion).
 */
(() => {
  if (customElements.get('uc-reveal')) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class UcReveal extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.uc-reveal__frame');
      this.handle = this.querySelector('.uc-reveal__handle');
      if (!this.frame || !this.handle) return;
      this.value = Number(this.dataset.start || 50);
      this.touched = false;
      this.set(this.value);

      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.onKey = this.onKey.bind(this);
      this.frame.addEventListener('pointerdown', this.onDown);
      this.handle.addEventListener('keydown', this.onKey);

      if (!reduceMotion.matches && 'IntersectionObserver' in window && !window.Shopify?.designMode) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) return;
            this.observer.disconnect();
            setTimeout(() => this.hint(), 500);
          },
          { threshold: 0.6 }
        );
        this.observer.observe(this.frame);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.frame?.removeEventListener('pointerdown', this.onDown);
      this.handle?.removeEventListener('keydown', this.onKey);
    }

    set(value, animate = false) {
      const v = Math.round(Math.min(Math.max(value, 0), 100) * 10) / 10;
      this.value = v;
      this.classList.toggle('is-animating', animate && !reduceMotion.matches);
      this.style.setProperty('--pos', `${v}%`);
      const rounded = Math.round(v);
      this.handle.setAttribute('aria-valuenow', String(rounded));
      const template = this.handle.dataset.valuetext;
      if (template) this.handle.setAttribute('aria-valuetext', template.replace('%p', String(100 - rounded)));
      this.dataset.edge = v < 12 ? 'start' : v > 88 ? 'end' : '';
    }

    fromPointer(event) {
      const rect = this.frame.getBoundingClientRect();
      return ((event.clientX - rect.left) / rect.width) * 100;
    }

    onDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.touched = true;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.pointerId = event.pointerId;
      // Op touch pas "grijpen" als de beweging horizontaal is, zodat verticaal scrollen blijft werken.
      this.dragging = event.pointerType !== 'touch';
      if (this.dragging) this.grab(event);
      this.frame.addEventListener('pointermove', this.onMove);
      this.frame.addEventListener('pointerup', this.onUp);
      this.frame.addEventListener('pointercancel', this.onUp);
    }

    grab(event) {
      this.dragging = true;
      this.classList.add('is-dragging');
      try {
        this.frame.setPointerCapture(this.pointerId);
      } catch (e) {
        /* niet alle pointers kunnen gecaptured worden */
      }
      this.set(this.fromPointer(event), event.type === 'pointerdown' && event.target !== this.handle && !this.handle.contains(event.target));
    }

    onMove(event) {
      if (!this.dragging) {
        const dx = Math.abs(event.clientX - this.startX);
        const dy = Math.abs(event.clientY - this.startY);
        if (dx > 6 && dx > dy) this.grab(event);
        else if (dy > 8) this.onUp();
        return;
      }
      event.preventDefault();
      this.set(this.fromPointer(event));
    }

    onUp(event) {
      if (event && !this.dragging && event.type === 'pointerup' && event.pointerType === 'touch') {
        // korte tik: spring naar die plek
        this.set(this.fromPointer(event), true);
      }
      this.dragging = false;
      this.classList.remove('is-dragging');
      this.frame.removeEventListener('pointermove', this.onMove);
      this.frame.removeEventListener('pointerup', this.onUp);
      this.frame.removeEventListener('pointercancel', this.onUp);
    }

    onKey(event) {
      const steps = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5, PageDown: -10, PageUp: 10 };
      let next = null;
      if (event.key in steps) next = this.value + steps[event.key] * (event.shiftKey ? 2 : 1);
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = 100;
      if (next === null) return;
      event.preventDefault();
      this.touched = true;
      this.set(next, true);
    }

    hint() {
      if (this.touched) return;
      const start = this.value;
      const frames = [start - 18, start + 12, start];
      frames.forEach((v, i) =>
        setTimeout(() => {
          if (!this.touched) this.set(v, true);
        }, i * 650)
      );
    }
  }

  customElements.define('uc-reveal', UcReveal);
})();
